import { useCallback, useEffect, useMemo, RefObject } from 'react';
// import { useOnClickOutside } from '@hooks/useOnClickOutside';
import { useAssetViewerState } from '@hooks/useAssetViewerState';
import { isCommentFormOpen } from '@pages/MediaViewer/helpers/drawToolShape';
import { isModalOpenOver } from '@pages/MediaViewer/helpers/modal';

export const PANEL_OVERLAY_SELECTOR = [
  '.media_viewer_menu',
  '.panel-section_head-overlay',
  '.media_viewer_tooltip',
  '.comment_mentions',
  '.comment_editor_mentions_list',
  '.ant-dropdown',
  '.ant-select-dropdown',
  '.ant-picker-dropdown',
  '.compare_version_tools_container',
  '.compare-versions-modal_head',
  '.app-tour-assist',
  '.app-tour-highlighted-mask',
  '.shapes_drawing_tools_container',
  '.color_picker_overlay',
  '.shape_picker_overlay',
  '.video_control_bar',
  '.media_viewer_panel-action',
  '.media_viewer_sidebar',
  '.media_viewer_sidebar-menu',
  '.media_viewer_sidebar-menu-btn',
  '.media_viewer_sidebar-content',
  '.panel-section',
  '.media_viewer_pdf-sidebar',
  '.media_viewer_pdf-actions',
  '.task-modal',
  '.reactour__mask',
  '.reactour__helper',
  '.reactour__close'
].join(', ');

type SidebarPanelDismissHandlerProps = {
  sidebarRef: RefObject<HTMLDivElement>;
  isOpen: boolean;
  isPinned: boolean;
  isLgWidth: boolean;
  onClose(): void;
};

function SidebarPanelDismissHandler({
  sidebarRef,
  isOpen,
  isPinned,
  isLgWidth,
  onClose
}: SidebarPanelDismissHandlerProps) {
  const {
    isCommentFieldFocused,
    commentText,
    canvasMode,
    selectedCommentId,
    playerTime,
    documentHighlight,
    selectComment,
    setDocumentHighlight
  } = useAssetViewerState();

  // A comment viewed (not edited or replied to): Esc closes it before the
  // panel. The floating comment closes it itself when shown (before this),
  // but it is hidden while the comments panel is open
  const isViewingComment =
    !!selectedCommentId &&
    !isCommentFormOpen(selectedCommentId) &&
    canvasMode === 'view';

  const canDismissSidebarPanel = useMemo(() => {
    if (isCommentFieldFocused) return false;
    if (commentText?.trim()) return false;
    if (canvasMode === 'edit') return false;
    if (selectedCommentId === 'new' || selectedCommentId === 'reply') {
      return false;
    }
    if (playerTime[1] != null) return false;
    if (documentHighlight?.text?.trim()) return false;
    return true;
  }, [
    isCommentFieldFocused,
    commentText,
    canvasMode,
    selectedCommentId,
    playerTime,
    documentHighlight
  ]);

  const closePanelIfAllowed = useCallback(() => {
    if (!isOpen || isPinned || !isLgWidth || !canDismissSidebarPanel) {
      return false;
    }
    onClose();
    return true;
  }, [isOpen, isPinned, isLgWidth, canDismissSidebarPanel, onClose]);

  useEffect(() => {
    const canClosePanel = isLgWidth && isOpen && !isPinned;
    if (!isViewingComment && !canClosePanel) return undefined;

    const onKeyDown = (e: KeyboardEvent) => {
      // Not when Esc closed something else, e.g. the floating comment
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      // E.g. the versions comparison over the media viewer handles it
      if (isModalOpenOver(sidebarRef.current)) return;
      if (isViewingComment) {
        e.preventDefault();
        selectComment(null);
        setDocumentHighlight(null);
        return;
      }
      // Handled: not also closing e.g. the versions comparison
      if (closePanelIfAllowed()) e.preventDefault();
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [
    sidebarRef,
    isLgWidth,
    isOpen,
    isPinned,
    isViewingComment,
    closePanelIfAllowed,
    selectComment,
    setDocumentHighlight
  ]);

  // TODO: Close sidebar on click outside — temporarily disabled
  // useOnClickOutside(sidebarRef, (e) => {
  //   if (!isOpen || isPinned || !isLgWidth) return;
  //   if (e.target.closest(PANEL_OVERLAY_SELECTOR)) return;
  //   closePanelIfAllowed();
  // });

  return null;
}

export default SidebarPanelDismissHandler;
